import React, { useCallback, useEffect, useState, useRef } from 'react';
import { POST } from 'api/index';
import { ChatResponseInterface, ParagraphInterface, UserInputInterface } from 'entities/interfaces';
import ChatComponent from 'ui/components/chat/ChatComponent';
import MobileChatComponent from 'ui/components/chat/MobileChatComponent';
import useAlert from 'hooks/AlertHook';
import { fetchChatGPTResponse } from 'api/chatGPT';
import { usePoints } from "context/PointsContext";
import Header from "ui/components/shared/Header";
import { useChatNodes } from 'hooks/useChatNode';
import { useChatFeedback } from 'hooks/useChatFeedback';
import { useChatProgress } from 'hooks/useChatProgress';
import { useChatLoader } from 'hooks/useChatLoader';

const SkillsChatWrapper = ({ active, updateProgress, mobileMode = false }: any) => {
    const { setAlert } = useAlert();
    const token = window.localStorage.getItem("currentUserToken");
    const [nodesData, setNodesData] = useState<ParagraphInterface[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [calledNodes, setCalledNodes] = useState<number[]>([]);
    const [chapter, setChapter] = useState<{
        id: string
        title: string
        num: number,
        isBookmarked: boolean,
        isLastSection: boolean,
        thumbnail?: string,
    }>();
    const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);
    const [typingAllowed, setTypingAllowed] = useState(false);
    const [processedChatMessages, setProcessedChatMessages] = useState<Set<string>>(new Set());
    const current = { chapter: '', section: '' };
    const [isWaitingForInput, setIsWaitingForInput] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [nodesProgress, setNodesProgress] = useState([]);
    const [lastChatNodeOutputs, setLastChatNodeOutputs] = useState<string | null>(null);
    const { addPoints, updateTotalPoints } = usePoints();
    const [chatQueue, setChatQueue] = useState<ParagraphInterface[]>([]);
    const [currentChatNode, setCurrentChatNode] = useState<ParagraphInterface | null>(null);
    const [clickedIndexes, setClickedIndexes] = useState<Set<number>>(() => {
        const savedIndexes = localStorage.getItem('clickedIndexes');
        return savedIndexes ? new Set(JSON.parse(savedIndexes)) : new Set();
    });;
    const [calledOutputs, setCalledOutputs] = useState<Set<string>>(new Set());
    const [currentChapterImage, setCurrentChapterImage] = useState<string>(' ');
    
    const [npsSubmitted, setNpsSubmitted] = useState(() => {
        return localStorage.getItem(`npsSubmitted_${active?.id}`) === "true";
    });
    const { handleFeedback } = useChatFeedback(
        token || '',
        active?.id,
        setNpsSubmitted,
        setAlert
    );
    const [currentChapterTitle, setCurrentChapterTitle] = useState<string | undefined>();
    const activeCourseIdRef = useRef<string | null>(null);
    const sessionIdRef = useRef<string>('');
    const timerRef      = useRef<NodeJS.Timeout | null>(null);  
    const activeIdRef   = useRef<string>();  

    useEffect(() => {
      activeIdRef.current = active.id;           
    }, [active.id]);

    const {
        fetchNodeProgress,
        handleSaveProgress,
        loadChatProgress
    } = useChatProgress(token || '', sessionIdRef, activeCourseIdRef);

    const saveLastChatMessage = useCallback((skillId: string, message: ParagraphInterface | any) => {
        try {
            if (!message || message.type === 'next') {
                return;
            }
            
            let displayText = '';
            
            // Exit/NPS Survey behandeln
            if (message.type === 'exit') {
                if (chapter?.isLastSection) {
                    displayText = npsSubmitted ? 'Kurs abgeschlossen' : 'Feedback ausfüllen';
                } else {
                    displayText = 'Nächstes Kapitel verfügbar';
                }
            }
            // Spezielle Behandlung für verschiedene Message-Typen
            else if (message.type === 'mc' || (message.type === 'io' && (message.title === 'Multiple Choice' || message.title === 'Multiple-Choice'))) {
                displayText = 'Multiple Choice Aufgabe bearbeitet';
            } else if (message.type === 'io' && (message.title === 'Drag-and-Drop' || message.title === 'Drag-and-Drop-Multiple')) {
                displayText = 'Drag & Drop Aufgabe bearbeitet';
            } else if (message.type === 'io' && (message.title === 'Sort-the-Paragraph' || message.title === 'Sort')) {
                displayText = 'Sortieraufgabe bearbeitet';
            } else if (message.type === 'io' && message.title === 'GPT') {
                displayText = 'GPT Aufgabe bearbeitet';
            } else if (message.type === 'userInput') {
                let content = message.content || '';
                try {
                    displayText = decodeURIComponent(content);
                } catch (e) {
                    displayText = content;
                }
                displayText = displayText.replace(/<[^>]*>/g, '').trim();
            } else if (message.type === 'response' || message.type === 'chat' || message.type === 'io') {
                let content = message.content || (message as any).feedback || '';
                
                if (typeof content === 'object') {
                    displayText = 'Interaktive Aufgabe bearbeitet';
                } else {
                    try {
                        displayText = decodeURIComponent(content);
                    } catch (e) {
                        displayText = content;
                    }
                    displayText = displayText.replace(/<[^>]*>/g, '').trim();
                }
            } else {
                displayText = 'Aktivität abgeschlossen';
            }
            
            if (!displayText || displayText === '') {
                return;
            }
            
            const chatPreview = {
                content: displayText,
                timestamp: new Date().toISOString(),
                type: message.type,
                id: message.id
            };
            localStorage.setItem(`lastChatMessage_${skillId}`, JSON.stringify(chatPreview));
            
            window.dispatchEvent(new CustomEvent('chatMessageUpdate', { 
                detail: { skillId, chatPreview } 
            }));
        } catch (error) {
            console.error('Error saving last chat message:', error);
        }
    }, [chapter, npsSubmitted]);

    useEffect(() => {
        if (active?.id) {
            setTimeout(() => {
                const storedValue = localStorage.getItem(`npsSubmitted_${active.id}`);
                setNpsSubmitted(storedValue === "true");
            }, 100);
        }
    }, [active.id]);

   useEffect(() => {
        return () => {
            setNodesData([]);
            setChapter(undefined);
            setNpsSubmitted(false);
            setTypingAllowed(false);
        };
    }, [active.id]);

    useEffect(() => {
        if (chapter && chapter.title) {
            setCurrentChapterTitle(chapter.title);
            
        }
        if(chapter && chapter.thumbnail) {
            setCurrentChapterImage(chapter.thumbnail);
        }
    }, [chapter]);

    useEffect(() => {
        const unprocessedChats = nodesData.filter(
            (node) => node.type === 'chat' && !processedChatMessages.has(String(node.id))
        );
        setChatQueue(unprocessedChats);
    }, [nodesData]);

    useEffect(() => {
        if(nodesData[nodesData.length - 1]?.type === 'chat') {
            setCurrentChatNode(nodesData[nodesData.length - 1]);
        }
      if (!currentChatNode) return;

      (async () => {
        const hist = await loadChatProgress(
          currentChatNode.id,
          active.id,
          sessionIdRef.current
        );
        setTypingAllowed(!hist);
      })();
    }, [currentChatNode, nodesData]);

    useEffect(() => {
        if (chatQueue.length > 0) {
            const nextChat = chatQueue[0];
            setProcessedChatMessages((prev) => new Set(prev).add(String(nextChat.id)));
            setIsWaitingForInput(true);

            setChatQueue((queue) => queue.slice(1));
        }
    }, [chatQueue]);

    useEffect(() => {
        const unprocessedChatMessages = nodesData.filter(
            (node) => node.type === 'chat' && !processedChatMessages.has(String(node.id))
            
        );
        if (unprocessedChatMessages.length > 0) {
            const nextChatMessage = unprocessedChatMessages[0];
            setCurrentChatNode(nextChatMessage);
            setProcessedChatMessages((prev) => new Set(prev).add(String(nextChatMessage.id)));
            setIsWaitingForInput(true);
        }
    }, [nodesData]);

    useEffect(() => {
        if (currentChatNode) {
            loadChatProgress(currentChatNode.id, active.id, sessionIdRef.current).then((exists) => {
                if (!exists) {
                   setTypingAllowed(true);
                }
            });
        }
    }, [currentChatNode]);

    function parseContent(node: ParagraphInterface) {
        const userName = localStorage.getItem('currentUserName')?.split(' ')[0] || '';
        const decodeMessage = (message: string) => {
            const decodedMessage = decodeURIComponent(message);
            return decodedMessage.replaceAll('@user.name', userName);
        };

        try {
            if ((node.type === 'mc' || node.type === 'io') && node.content.startsWith('%7B%0A%20%20%22type%22%3A%20%22mc')) {
                const decodedContent = JSON.parse(decodeMessage(decodeURIComponent(node.content)));
                const isSingle = node.title === 'Single Choice' || decodedContent.options?.length === 2;

                return {
                    ...node,
                    content: {
                        ...decodedContent,
                        isSingleChoice: isSingle,
                    },
                    selectedOptions: node.selectedOptions || [],
                    progressState: node.progressState,
                    placedElements: node.placedElements || {},
                    points: node.points ?? 0,
                    requiresInput: true,
                };
            }

            if (node.type === 'chat') {
                return {
                    ...node,
                    content: decodeMessage(decodeURIComponent(node.content)),
                    prompt: decodeMessage(decodeURIComponent(node.prompt)),
                    requiresInput: true,
                };
            }

            if (node.type === 'io' && [
                "Drag-and-Drop", "DnD", "Drag-And-Drop", "Drag-and-Drop-Multiple"
            ].includes(node.title)) {
                return {
                    ...node,
                    title: node.title === "Drag-and-Drop-Multiple" ? node.title : "Drag-and-Drop",
                    content: decodeMessage(decodeURIComponent(node.content)),
                    placedElements: node.placedElements || {},
                    progressState: node.progressState,
                    points: node.points ?? 0,
                    requiresInput: true,
                };
            }

            if (node.type === 'io' && ["Sort-the-Paragraph", "Sort"].includes(node.title)) {
                return {
                    ...node,
                    content: decodeMessage(decodeURIComponent(node.content)),
                    placedElements: node.placedElements || {},
                    progressState: node.progressState,
                    points: node.points ?? 0,
                    requiresInput: true,
                };
            }
        } catch (error) {
            console.error("Fehler beim Parsen des Inhalts:", error);
        }

        return node;
    }

    const bookmarkChapter = async (id: string, value: number) => {
        const body = new URLSearchParams();
        if (token)
            body.append('userToken', token);
        body.append('option', id);
        body.append('content', value.toString());
        const response = await POST('setBookmark', body)
        if (response.status === 200) {
            chapter && setChapter({ ...chapter, isBookmarked: value === 1 })
        }
    }

    const clearProgress = async () => {
        const resetBody = new URLSearchParams();
        resetBody.append('userToken', token || '');
        resetBody.append('option', active.id);

        const response = await POST('clearUserProgress', resetBody);
        if (response.status === 200) {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }

            setIsLoading(true);
            setCalledNodes([]);
            setNodesData([]);
            setChapter(undefined);
            setClickedIndexes(new Set());
            localStorage.removeItem('clickedIndexes');
            localStorage.removeItem(`npsSubmitted_${active.id}`);
            localStorage.removeItem(`lastChatMessage_${active.id}`);

            Object.keys(localStorage)
            .filter(k => k.startsWith("chatBackup_"))
            .forEach(k => localStorage.removeItem(k));
            
            await getChatData(active.id, sessionIdRef.current);
            setNpsSubmitted(false);

            setAlert({ color: 'success', message: 'Course progress cleared!' });
        }
    };

    const getNext = useCallback(
        async (outputs: string) => {
          const courseIdAtCall = activeIdRef.current;
          const [nextParagraphId, delay = "0"] = outputs.split("@");
      
          setIsLoading(true);
      
          const body = new URLSearchParams();
          body.append("option", nextParagraphId);
      
          const start = Date.now();
          const paragraphsResp = await POST("getParagraph", body);
          const apiTime = Date.now() - start;
          
          const remainingDelay = Math.max(
            Math.max(Number(delay) * 1000, 1500) - apiTime,
            0
          );

          if (timerRef.current) clearTimeout(timerRef.current);

          timerRef.current = setTimeout(async () => {
            if (courseIdAtCall !== activeIdRef.current) return;
      
            setIsLoading(false);
      
            const paragraph       = paragraphsResp.result;
            const parsedParagraph = parseContent(paragraph);
            
            setNodesData(prev => {
                const newData = prev.some(n => n.id === paragraph.id) ? prev : [...prev, parsedParagraph];
                
                if (parsedParagraph && active?.id) {
                    saveLastChatMessage(active.id, parsedParagraph);
                }
                
                return newData;
            });
      
            setLastChatNodeOutputs(null);
            setIsWaitingForInput(false);
      
            await markParagraph(paragraph.id);
          }, remainingDelay);
        },
        [saveLastChatMessage, active?.id] 
      );
      
    useEffect(() => () => {
        if (timerRef.current) clearTimeout(timerRef.current);
    }, [active.id]);

    const markParagraph = async (paragraphId: string) => {
        const body = new URLSearchParams();
        body.append('userToken', token || '');
        body.append('option', paragraphId);
        const response = await POST('markParagraph', body);
        updateTotalPoints();
    }

    const handleNextChapter = async (sectionId: number) => {
        updateProgress()
        
        // Nach Progress-Update prüfen ob Kurs abgeschlossen ist
        setTimeout(() => {
            // Event für Kurs-Abschluss triggern
            window.dispatchEvent(new CustomEvent('courseCompleted', { 
                detail: { skillId: active.id } 
            }));
        }, 500);
        
        setIsLoading(true)
        setCalledNodes([])
        setNodesData([])
        setChapter(undefined)
        setProcessedChatMessages(new Set())
        setChatQueue([])
        setCurrentChatNode(null)
        setCalledOutputs(new Set())
        getChatData(active.id, sessionIdRef.current);
    }

    const reportParagraph = async (paragraphId: string, content: string) => {
        const body = new URLSearchParams();
        body.append('userToken', token || '');
        body.append('option', paragraphId.toString());
        body.append('content', content.toString());
        await POST('setParagraphFeedback', body)
    }

    useEffect(() => {
        if (!active || !active.id) return;

        sessionIdRef.current = `${active.id}_${Date.now()}`;
        activeCourseIdRef.current = active.id;

        setProcessedChatMessages(new Set());
        setChatQueue([]);
        setCurrentChatNode(null);
        setIsLoading(true);
        setCalledNodes([]);
        setNodesData([]);
        setChapter(undefined);
        setNpsSubmitted(false);

        getChatData(active.id, sessionIdRef.current);
    }, [active, mobileMode]);

    const saveChatProgress = async (nodeId: number, userResponse: string, gptResponse: string) => {
        if (!userResponse || !gptResponse) {
            console.warn("⚠️ Ungültiger Inhalt gespeichert!", { userResponse, gptResponse });
        }
        if (!userResponse?.trim()) {
            console.warn("⚠️ Kein User-Text vorhanden – wird nicht gespeichert!");
            return;
        }
        const body = new URLSearchParams();
        body.append('userToken', localStorage.getItem('currentUserToken') || '');
        body.append('nodeId', nodeId.toString());
        body.append('userResponse', userResponse);
        body.append('gptResponse', gptResponse);

        const cacheKey = `chatBackup_${nodeId}`;

        try {
            const response = await POST('setChatProgress', body);

            if (!response || response.status !== 200) {
                console.warn("API-Fehler beim Speichern – speichere lokal:", response);
                localStorage.setItem(cacheKey, JSON.stringify({ userResponse, gptResponse }));
            } else {
                localStorage.setItem(cacheKey, JSON.stringify({ userResponse, gptResponse }));
            }
        } catch (error) {
            console.error("Netzwerkfehler beim Speichern – speichere lokal:", error);
            localStorage.setItem(cacheKey, JSON.stringify({ userResponse, gptResponse }));
        }
    };

    const { handleUserInput } = useChatNodes({
        nodesData,
        setNodesData,
        currentChatNode,
        setCurrentChatNode,
        getNext,
        setTypingAllowed,
        setIsLoading,
        setLastChatNodeOutputs,
        saveChatProgress,
        saveLastChatMessage,
        token: token || '',
        activeIdRef,
        activeId: active?.id,
    })

    const { getChatData } = useChatLoader({
        token: token || '',
        current,
        setChapter,
        setIsLoading,
        setNodesData,
        parseContent,
        fetchNodeProgress,
        markParagraph,
        loadChatProgress,
        activeCourseIdRef,
        sessionIdRef,
    });

    useEffect(() => {
        if (nodesData && nodesData.length > 0) {
            const last = nodesData[nodesData.length - 1]
            
            if (last.requiresInput) {
                setIsWaitingForInput(true);
                return;
            }
            setIsWaitingForInput(false);
            if (last.type !== 'next' && last.outputs && last.outputs.split(',').length === 1) {
                if (!calledNodes.includes(last.id)) {
                    setCalledNodes(calledNodes => [...calledNodes, last.id])
                    getNext(last.outputs);
                }
            }
            if (last.type == 'response' && lastChatNodeOutputs) {

                if (!calledOutputs.has(lastChatNodeOutputs)) {
                        setCalledOutputs(new Set(calledOutputs).add(lastChatNodeOutputs));
                        getNext(lastChatNodeOutputs);
                        setLastChatNodeOutputs(null);           
                      }
            }

            if(last.requiresInput && last.progressState) {
                console.log("saved Element ");
                getNext(last.outputs);
            }
        }

    }, [calledNodes, getNext, nodesData, active]);

    useEffect(() => {
        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [timeoutId]);

    const handleSubmit = () => {
        setIsSubmitted(true);
        setIsWaitingForInput(false);

        if (nodesData && nodesData.length > 0) {
            const last = nodesData[nodesData.length - 1];

            if (last.type !== 'next' && last.outputs && last.outputs.split(',').length === 1) {
                if (!calledNodes.includes(last.id)) {
                    setCalledNodes((calledNodes) => [...calledNodes, last.id]);

                    setTimeout(() => {
                        getNext(last.outputs);
                    }, 2000);
                }
            }
        }
    };

    useEffect(() => {
        setIsWaitingForInput(false);
        if (isSubmitted) {
            setIsSubmitted(false)
        }
    }, [isSubmitted]);

    return (
        <>
            {!mobileMode && (
                <Header
                    data={active}
                    chapterImage={currentChapterImage}
                    chapterTitle={currentChapterTitle}
                />
            )}

            {active && (
                mobileMode ? (
                    <MobileChatComponent
                        messagesData={nodesData}
                        onUserInput={(msg: string) => {
                            setTypingAllowed(false);   
                            handleUserInput(msg); 
                        }}
                        isLoading={isLoading}
                        handleNext={getNext}
                        onSubmit={handleSubmit}
                        handleSaveProgress={(data) =>
                            handleSaveProgress(nodesData, active.id, data, updateTotalPoints)
                        }
                        clickedIndexes={clickedIndexes}
                        setClickedIndexes={setClickedIndexes}
                        handleNextChapter={handleNextChapter}
                        handleFeedback={handleFeedback}
                        npsSubmitted={npsSubmitted}
                        setNpsSubmitted={setNpsSubmitted}
                        typingAllowed={typingAllowed}
                        chapter={chapter}
                    />
                ) : (
                    <ChatComponent
                        messagesData={nodesData}
                        date={new Date()}
                        sendDisabled
                        chapter={chapter}
                        bookmarkChapter={bookmarkChapter}
                        clearProgress={clearProgress}
                        handleNext={getNext}
                        isLoading={isLoading}
                        handleFeedback={handleFeedback}
                        handleNextChapter={handleNextChapter}
                        reportAllowed
                        reportParagraph={reportParagraph}
                        typingAllowed={typingAllowed}
                        onUserInput={(msg: string) => {
                            setTypingAllowed(false);   
                            handleUserInput(msg); 
                        }}
                        onSubmit={handleSubmit}
                        setClickedIndexes={setClickedIndexes}
                        clickedIndexes={clickedIndexes}
                        handleSaveProgress={(data) =>
                            handleSaveProgress(nodesData, active.id, data, updateTotalPoints)
                        }
                        setNpsSubmitted={setNpsSubmitted}
                        npsSubmitted={npsSubmitted}
                        mobileMode={false}
                    />
                )
            )}
        </>
    );

};

export default SkillsChatWrapper;